import React, { lazy, Suspense, useEffect } from 'react';
import styles from './Announcements.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
  navigateToSettings,
  openModal,
  toggleWelcomeGuideVisibility,
} from '@repo/redux/actions/ui';
import analytics from '@repo/utils/helpers/analytics';
import {
  ActionMergeIcon,
  ArrowRightIcon,
  CogIcon,
  MagnifyingGlassIcon,
  NavHomeIcon,
  StylizedUdoIcon,
} from '@repo/icons';
import { AsperandV2Icon } from '@repo/icons/TourIcons';
import { useNavigate } from 'react-router-dom';
import { selectGroup } from '@repo/redux/actions/groups';
import { GroupPill } from '@components/Contact/Groups';

const ReactMarkdown = lazy(() => import('react-markdown'));

const handleExternalLink = (_: string, e: MouseEvent) => {
  e?.stopPropagation();
  analytics.track(`Activity Link Tapped`);
};

const ReferralLink = ({ children }) => {
  const dispatch = useDispatch();

  return (
    <span className={styles.inlineBtn} onClick={() => dispatch(openModal('shareInvite'))}>
      {children}
    </span>
  );
};

const BookletLink = ({ children }) => {
  const dispatch = useDispatch();

  return <a onClick={() => dispatch(toggleWelcomeGuideVisibility(true))}>{children}</a>;
};

const SettingsIntegrationsLink = () => {
  const dispatch = useDispatch();

  return (
    <span
      className={styles.inlineBtn}
      onClick={() => {
        dispatch(navigateToSettings({ panel: 'integrations' }));
      }}
    >
      <span className={styles.inlineBtnPathSection}>
        <StylizedUdoIcon height="16" width="14" color="textPrimary" />
        App Menu
      </span>
      <ArrowRightIcon width="12" height="10" className={styles.inlineBtnArrow} />
      <span className={styles.inlineBtnPathSection}>
        <CogIcon height="16" width="14" strokeWidth="1.4" color="textPrimary" />
        Settings
      </span>
      <ArrowRightIcon width="12" height="10" className={styles.inlineBtnArrow} />
      <span className={styles.inlineBtnPathSection}>
        <AsperandV2Icon height="16" width="14" color="textPrimary" />
        Integrations
      </span>
    </span>
  );
};

const SettingsHomeLink = () => {
  const dispatch = useDispatch();

  return (
    <span
      className={styles.inlineBtn}
      onClick={() => {
        dispatch(navigateToSettings({ panel: 'home' }));
      }}
    >
      <span className={styles.inlineBtnPathSection}>
        <StylizedUdoIcon height="16" width="14" color="textPrimary" />
        App Menu
      </span>
      <ArrowRightIcon width="12" height="10" className={styles.inlineBtnArrow} />
      <span className={styles.inlineBtnPathSection}>
        <CogIcon height="16" width="14" strokeWidth="1.4" color="textPrimary" />
        Settings
      </span>
      <ArrowRightIcon width="12" height="10" className={styles.inlineBtnArrow} />
      <span className={styles.inlineBtnPathSection}>
        <NavHomeIcon height="16" width="11" strokeWidth="2" color="textPrimary" />
        Home
      </span>
    </span>
  );
};

const StarredGroupLink = () => {
  const starredGroup = useSelector(selectGroup('starred'));
  return <GroupPill className={styles.starredGroupLink} group={starredGroup} asLink />;
};

const SearchLink = () => {
  const navigate = useNavigate();

  return (
    <span
      className={styles.inlineBtn}
      onClick={() => {
        navigate('/search');
      }}
    >
      <span className={styles.inlineBtnPathSection}>
        <MagnifyingGlassIcon height="16" width="12" color="textPrimary" />
        Search
      </span>
    </span>
  );
};

const DuplicatesLink = () => {
  const navigate = useNavigate();

  return (
    <span
      className={styles.inlineBtn}
      onClick={() => {
        navigate('/people/duplicates');
      }}
    >
      <span className={styles.inlineBtnPathSection}>
        <ActionMergeIcon height="16" width="15" color="textPrimary" hoverOpacity={false} />
        Duplicates
      </span>
    </span>
  );
};

const ClayLink = ({ children, ...props }) => {
  // @ts-ignore
  const [_, actionOrLocation, id] = (props.node?.properties?.href || '').match(
    /clay-app:\/\/([a-zA-Z0-9\-]+)\/?([a-zA-Z0-9\-]+)?/,
  );

  switch (actionOrLocation) {
    case 'referral':
      return <ReferralLink>{children}</ReferralLink>;

    case 'welcomeBooklet':
      return <BookletLink>{children}</BookletLink>;

    case 'view-settings-integrations':
      return <SettingsIntegrationsLink />;

    case 'view-settings-home':
      return <SettingsHomeLink />;

    case 'view-starred':
      return <StarredGroupLink />;

    case 'view-search':
      return <SearchLink />;

    case 'view-potential-duplicates':
      return <DuplicatesLink />;

    default:
      return null;
  }
};

const renderLink = (
  // analyticsType: string | undefined,
  { children, ...props },
) => {
  if (props.node?.properties?.href?.startsWith('clay-app://')) {
    return <ClayLink {...props}>{children}</ClayLink>;
  }

  if (props.node?.properties?.href === 'null') {
    return children;
  }

  return (
    <a onClick={handleExternalLink.bind(null, 'Activity')} href={props.href} target="_blank">
      <span className={`${styles.link} feed-link`}>{children}</span>
    </a>
  );
};

const renderCollapsedParagraphs = ({ children }) => {
  return <>{children}&nbsp;</>;
};

const renderCollapsedUnorderedList = ({ children }) => {
  return <>{children}</>;
};

const renderCollapsedOrderedList = ({ children }) => {
  return <>{children}</>;
};

const renderCollapsedListItem = ({ children }) => {
  return <>{children}&nbsp;</>;
};

const CollapsedParagraphContentWrapper = ({ children }) => <p>{children}</p>;

export default function renderMarkdown(content: string, collapseBlocks?: boolean) {
  const [plugins, setPlugins] = React.useState([]);
  const Wrapper = collapseBlocks ? CollapsedParagraphContentWrapper : React.Fragment;

  useEffect(() => {
    const loadRemarkGfm = async () => {
      const { default: remarkGfm } = await import('remark-gfm');
      setPlugins([remarkGfm]);
    };

    loadRemarkGfm();
  }, []);

  return (
    <Wrapper>
      <Suspense fallback={<div />}>
        <ReactMarkdown
          children={content}
          linkTarget="_blank"
          components={{
            a: renderLink,
            ...(collapseBlocks
              ? {
                  p: renderCollapsedParagraphs,
                  ul: renderCollapsedUnorderedList,
                  ol: renderCollapsedOrderedList,
                  li: renderCollapsedListItem,
                }
              : {}),
          }}
          remarkPlugins={plugins}
        />
      </Suspense>
    </Wrapper>
  );
}
